import { ITEMS, type ItemId } from '@/data/items';
import type { Inventory } from '@/world/inventory';
import { BAG_SIZE, type QuickBag } from '@/world/quickBag';

/**
 * The bag: quick access along the bottom of the screen.
 *
 * Each slot is an *assignment* — "turnip seeds" — and the count beside it is
 * read from the backpack every frame. Running out greys the slot rather than
 * clearing it, so a loadout survives being used up and the slot you reach for
 * without looking is still the slot you left there.
 *
 * Tapping an empty slot chooses what goes in it. Tapping the slot already in
 * hand reopens that chooser, which is how an assignment gets changed or
 * cleared without a separate edit mode.
 */

export interface ItemBarOptions {
  readonly onSelect: (index: number) => void;
  readonly onAssign: (index: number, id: ItemId | null) => void;
}

export class ItemBar {
  private readonly root: HTMLDivElement;
  private readonly slots: HTMLButtonElement[] = [];
  private readonly picker: HTMLDivElement;
  private lastSignature = '';
  private pickerFor: number | null = null;

  constructor(container: HTMLElement, private readonly options: ItemBarOptions) {
    this.root = document.createElement('div');
    this.root.className = 'item-bar';

    for (let i = 0; i < BAG_SIZE; i++) {
      const slot = document.createElement('button');
      slot.className = 'item-slot';
      slot.type = 'button';

      const label = document.createElement('span');
      label.className = 'item-label';
      const count = document.createElement('span');
      count.className = 'item-count';
      slot.append(label, count);

      slot.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        this.onSlotPressed(i);
      });

      this.slots.push(slot);
      this.root.appendChild(slot);
    }

    this.picker = document.createElement('div');
    this.picker.className = 'bag-assign';
    this.picker.hidden = true;

    container.append(this.root, this.picker);
  }

  private onSlotPressed(index: number): void {
    if (this.pickerFor !== null) {
      this.closePicker();
      return;
    }
    this.pending = index;
  }

  /** Set by a press, resolved on the next update when the data is to hand. */
  private pending: number | null = null;

  update(bag: QuickBag, backpack: Inventory): void {
    if (this.pending !== null) {
      const index = this.pending;
      this.pending = null;
      // An unassigned slot, or the one already in hand, opens the chooser.
      // Anything else is simply a change of what is held.
      if (bag.assignmentAt(index) === null || bag.selectedIndex === index) {
        this.openPicker(index, bag, backpack);
      } else {
        this.options.onSelect(index);
      }
    }

    // Counts move with every harvest, so the fingerprint covers them; the DOM
    // is still only touched when something a player can see actually changes.
    const signature = `${bag.selectedIndex}|${Array.from({ length: BAG_SIZE }, (_, i) => {
      const id = bag.assignmentAt(i);
      return id ? `${id}x${backpack.countOf(id)}` : '-';
    }).join(',')}`;

    if (signature === this.lastSignature) return;
    this.lastSignature = signature;

    for (let i = 0; i < BAG_SIZE; i++) {
      this.renderSlot(i, bag, backpack);
    }
  }

  private renderSlot(index: number, bag: QuickBag, backpack: Inventory): void {
    const element = this.slots[index]!;
    const id = bag.assignmentAt(index);
    const label = element.querySelector('.item-label')!;
    const count = element.querySelector('.item-count')!;

    element.classList.toggle('is-selected', index === bag.selectedIndex);
    element.classList.toggle('is-empty', id === null);

    if (!id) {
      label.textContent = '+';
      count.textContent = '';
      element.style.removeProperty('--tint');
      element.setAttribute('aria-label', `Bag slot ${index + 1}, empty. Choose an item`);
      element.classList.remove('is-out');
      return;
    }

    const definition = ITEMS[id];
    const held = backpack.countOf(id);

    label.textContent = definition.label;
    // Tools are unique, so a "1" beside them is noise.
    count.textContent = definition.stackSize > 1 ? String(held) : '';
    element.style.setProperty('--tint', definition.tint);
    // Assigned but spent: still yours, just nothing left to use.
    element.classList.toggle('is-out', held === 0);
    element.setAttribute(
      'aria-label',
      held === 0
        ? `Bag slot ${index + 1}, ${definition.name}, none left`
        : `Bag slot ${index + 1}, ${definition.name}, ${held}`,
    );
  }

  private openPicker(index: number, bag: QuickBag, backpack: Inventory): void {
    this.pickerFor = index;
    this.picker.replaceChildren();

    // Distinct items, since one bar slot per item — a pile split across two
    // backpack slots is still one thing to reach for.
    const seen = new Set<ItemId>();
    const choices: ItemId[] = [];
    for (let i = 0; i < backpack.capacity; i++) {
      const slot = backpack.slotAt(i);
      if (!slot || seen.has(slot.id)) continue;
      seen.add(slot.id);
      choices.push(slot.id);
    }

    for (const id of choices) {
      const definition = ITEMS[id];
      const option = document.createElement('button');
      option.className = 'bag-assign-option';
      option.type = 'button';
      option.style.setProperty('--tint', definition.tint);
      option.textContent = `${definition.name}${
        definition.stackSize > 1 ? ` x${backpack.countOf(id)}` : ''
      }`;
      option.addEventListener('click', () => {
        this.options.onAssign(index, id);
        this.closePicker();
      });
      this.picker.appendChild(option);
    }

    if (choices.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'bag-assign-empty';
      empty.textContent = 'Your backpack is empty';
      this.picker.appendChild(empty);
    }

    if (bag.assignmentAt(index) !== null) {
      const clear = document.createElement('button');
      clear.className = 'bag-assign-clear';
      clear.type = 'button';
      clear.textContent = 'Clear slot';
      clear.addEventListener('click', () => {
        this.options.onAssign(index, null);
        this.closePicker();
      });
      this.picker.appendChild(clear);
    }

    this.picker.hidden = false;
  }

  private closePicker(): void {
    this.pickerFor = null;
    this.picker.hidden = true;
    this.picker.replaceChildren();
    // Force the next update to redraw: an assignment may have just changed.
    this.lastSignature = '';
  }

  get isPickerOpen(): boolean {
    return this.pickerFor !== null;
  }

  destroy(): void {
    this.root.remove();
    this.picker.remove();
  }
}
